import { jsxs, jsx } from 'react/jsx-runtime';
import { canScrollTo } from '@flex-design-system/fx-utils/element';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Portal, Root, Trigger, Cancel, Action } from '@radix-ui/react-alert-dialog';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import classNames from 'classnames';
import { forwardRef, useRef, useState } from 'react';
import usePopperPresence from '../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../hooks/usePreventTransitionInteraction.mjs';
import { overlayStyle } from '../../utils/overlay.vanilla.mjs';
import { Flex } from '../layout/Flex.mjs';
import { ShakingAnimation } from '../layout/ShakingAnimation.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';
import TextBalancer from '../text-balancer/TextBalancer.mjs';
import { Body } from './components/Body.mjs';
import { Content } from './components/content/Content.mjs';
import { ContentInner } from './components/content/ContentInner.mjs';
import { ContentPositioner } from './components/content/ContentPositioner.mjs';
import { Description } from './components/Description.mjs';
import { Footer } from './components/Footer.mjs';
import { PortalContainer } from './components/PortalContainer.mjs';
import { Title } from './components/Title.mjs';
import { useDialogContext, DialogProvider } from './context.mjs';
import { scrollAreaViewportStyle, fogStyle } from './dialog.vanilla.mjs';

const DIALOG_OVERLAY_ATTRIBUTE = "data-flex-dialog-overlay";
const DIALOG_CONTENT_ATTRIBUTE = "data-flex-dialog-content";
const DialogImpl = (props) => {
  const {
    open: openProp,
    defaultOpen,
    onOpenChange,
    children,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const [portalContainerElement, setPortalContainerElement] = useState(void 0);
  return /* @__PURE__ */ jsx(
    DialogProvider,
    {
      open,
      onOpenChange: setOpen,
      onClose,
      portalContainerElement,
      onPortalContainerElementChange: setPortalContainerElement,
      children: /* @__PURE__ */ jsx(Root, { ...rootProps, open, onOpenChange: setOpen, children })
    }
  );
};
const StyledContent = styled(Content);
const StyledContentInner = styled(ContentInner);
const DialogContent = forwardRef(
  (props, ref) => {
    const {
      children,
      closeOnInteractOutside = true,
      onInteractOutside,
      overlayTheme = "light",
      css,
      className,
      style,
      portalContainer: portalContainerFromProps,
      zIndex,
      ...contentProps
    } = props;
    const role = closeOnInteractOutside ? "dialog" : "alertdialog";
    const {
      onOpenChange,
      portalContainerElement: portalContainerFromContext,
      open,
      onClose
    } = useDialogContext("DialogContent");
    const animationRef = useRef(null);
    const portalContainer = portalContainerFromProps ?? portalContainerFromContext;
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => !isPresent && onClose?.()
    });
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const composedRef = useComposedRefs(ref, presenceRef);
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    return /* @__PURE__ */ jsxs(Portal, { container: portalContainer, children: [
      /* @__PURE__ */ jsx(
        AlertDialogPrimitive.Overlay,
        {
          ...{ [DIALOG_OVERLAY_ATTRIBUTE]: "" },
          className: overlayStyle({ overlayTheme }),
          style: zIndexStyle,
          onClick: composeEventHandlers(onInteractOutside, (e) => {
            if (!closeOnInteractOutside) {
              animationRef.current?.triggerAnimation();
              return;
            }
            if (!e.defaultPrevented) {
              onOpenChange(false);
              return;
            }
          })
        }
      ),
      /* @__PURE__ */ jsx(
        ContentPositioner,
        {
          ...{ [DIALOG_CONTENT_ATTRIBUTE]: "" },
          style: zIndexStyle,
          children: /* @__PURE__ */ jsx(
            StyledContent,
            {
              ...contentProps,
              onKeyDownCapture: (e) => {
                preventTransitionInteraction.onKeyDownCapture?.(e);
                contentProps.onKeyDownCapture?.(e);
              },
              ref: composedRef,
              role,
              children: /* @__PURE__ */ jsx(ShakingAnimation, { ref: animationRef, children: /* @__PURE__ */ jsx(
                StyledContentInner,
                {
                  className,
                  style: { ...style, ...preventTransitionInteraction.style },
                  css,
                  children
                }
              ) })
            }
          )
        }
      )
    ] });
  }
);
const StyledBody = styled(Body);
const DialogBody = forwardRef(
  (props, ref) => {
    const { children, css, className, viewportStyle, ...bodyProps } = props;
    const [scrollAreaViewportElement, setScrollAreaViewportElement] = useState(null);
    const canBottomFogVisible = canScrollTo(
      scrollAreaViewportElement,
      "bottom"
    );
    return /* @__PURE__ */ jsx(StyledBody, { ref, ...bodyProps, children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
      /* @__PURE__ */ jsx(
        ScrollArea.Viewport_Legacy,
        {
          css,
          style: viewportStyle,
          className: classNames(scrollAreaViewportStyle, className),
          ref: setScrollAreaViewportElement,
          children: /* @__PURE__ */ jsx(Flex, { direction: "column", align: "center", style: { gap: 10 }, children })
        }
      ),
      canBottomFogVisible && /* @__PURE__ */ jsx(
        ScrollArea.Fog,
        {
          className: fogStyle,
          side: "bottom",
          color: "#FFFFFF"
        }
      )
    ] }) });
  }
);
const DialogTitle = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(StyledTitle, { ...props, asChild: true, ref, children: /* @__PURE__ */ jsx(TextBalancer, { as: "h2", children }) });
});
const DialogDescription = forwardRef(({ children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(StyledDescription, { ...props, asChild: true, ref, children: /* @__PURE__ */ jsx(TextBalancer, { as: "p", children }) });
});
const StyledFooter = styled(Footer);
const DialogFooter = forwardRef(
  ({ orientation = "horizontal", ...props }, ref) => {
    return /* @__PURE__ */ jsx(StyledFooter, { ref, orientation, ...props });
  }
);
const StyledTitle = styled(Title);
StyledTitle.toString = () => '[data-scope="dialog"][data-part="title"]';
const StyledDescription = styled(Description);
StyledDescription.toString = () => '[data-scope="dialog"][data-part="description"]';
const StyledPortalContainer = styled(PortalContainer);
StyledPortalContainer.toString = () => '[data-scope="dialog"][data-part="portal-container"]';
const Dialog = DialogImpl;
Dialog.Content = DialogContent;
Dialog.Trigger = Trigger;
Dialog.Cancel = Cancel;
Dialog.Action = Action;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;
Dialog.UnbalancedTitle = StyledTitle;
Dialog.UnbalancedDescription = StyledDescription;
Dialog.PortalContainer = StyledPortalContainer;

export { DIALOG_CONTENT_ATTRIBUTE, DIALOG_OVERLAY_ATTRIBUTE, Dialog };
//# sourceMappingURL=Dialog.mjs.map
